import { mergeAttributes } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import { Slice } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { VueNodeViewRenderer } from '@tiptap/vue-3';

import AttachmentImageNode from './AttachmentImageNode.vue';
import { typeAfterSelectedNode } from './caret';
import { restorePastedFiles } from './pasted-image';

/**
 * Drop-in replacement for the stock Image node (same `image` name, so existing
 * documents keep working and UEditor's markdown pipeline is unaffected —
 * register it with `:image="false"` on UEditor to avoid a duplicate).
 *
 * Images uploaded through the editor carry a `fileId` and serialize their
 * `src` as the stable sentinel `omb-file://<fileId>` — presigned URLs must
 * never end up in persisted payloads. External images (plain `src`, no
 * `fileId`) render unchanged. A pasted copy of an uploaded image is the one
 * thing that arrives looking external; `transformPasted` turns it back into
 * its file (see `restorePastedFiles`).
 */
export const AttachmentImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      fileId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-file-id'),
        renderHTML: (attributes) => (attributes.fileId ? { 'data-file-id': attributes.fileId } : {}),
      },
      uploadId: {
        default: null,
        renderHTML: () => ({}),
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    const attributes = node.attrs.fileId
      ? { ...HTMLAttributes, src: `omb-file://${node.attrs.fileId}` }
      : HTMLAttributes;

    return ['img', mergeAttributes(this.options.HTMLAttributes, attributes)];
  },

  addNodeView() {
    return VueNodeViewRenderer(AttachmentImageNode);
  },

  addProseMirrorPlugins() {
    return [
      ...(this.parent?.() ?? []),
      typeAfterSelectedNode(this.type),
      new Plugin({
        key: new PluginKey('attachmentImagePaste'),
        props: {
          transformPasted: (slice) =>
            new Slice(restorePastedFiles(slice.content), slice.openStart, slice.openEnd),
        },
      }),
    ];
  },
});
